import { Spinner } from '@/components/glitch-ui/spinner';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import {
  ArchiveRestore,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Globe,
  MoreHorizontal,
  Plus,
  RefreshCw,
  Sparkles,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useUserProfile } from '@/contexts/user.context';
import { useTestMode } from '@/hooks/useTestMode';
import {
  businessLocationsQueryOptions,
  locationsByIdsQueryOptions,
  useResetLocationToAgentMutation,
} from '@/lib/query-options';
import { radiusInUnit } from '@/store';

import { LocationRow } from './location-row';
import { TruncationBanner } from './truncation-banner';
import { DetectorStatus } from './detector-status';
import { MapLocationDialog } from './map-location-dialog';
import { ResolveOrphanDialog } from './resolve-orphan-dialog';
import type { RadiusFormValue } from './radius-form';

import {
  UserRole,
  type Business,
  type BusinessLocationRead,
  type BusinessLocationReconcileItem,
  type Location,
} from '@/lib/types';
import { formatLocationName } from '@/lib/utils';

/**
 * Save controller the parent's atomic "Save Changes" (#5401) uses instead of a self-contained
 * reconcile call: it pulls the desired location set to fold into the one request, then adopts the
 * saved active set as the new baseline.
 */
export interface LocationsSaveControl {
  /** The full desired active location set to reconcile in the atomic save. */
  getPayload: () => BusinessLocationReconcileItem[];
  /** Adopt the server's saved active set as the new baseline (clears dirty) + refresh caches. */
  markSaved: (saved: BusinessLocationRead[]) => void;
}

interface BusinessLocationsProps {
  business: Business;
  /** Reports whether the locations draft differs from the saved server set so the
   * page can light up the shared "Save Changes" button. */
  onDirtyChange?: (dirty: boolean) => void;
  /** The page assigns a flush function here; calling it persists the draft via the
   * single reconcile endpoint. Mirrors the Budget/KPI delegated-save pattern. */
  saveRef?: React.MutableRefObject<LocationsSaveControl | null>;
}

type RadiusSort = 'name' | 'radius' | 'created';

/** Draft row: a `BusinessLocationRead` plus, for not-yet-saved adds, a `_new`
 * marker and the catalog selection-data passthrough the backend needs to seed an
 * uncached geo-target. New rows carry a `new:`-prefixed synthetic uuid for React
 * keys; the prefix is stripped (uuid omitted) when building the save payload. */
type DraftRow = BusinessLocationRead & {
  _new?: boolean;
  _meta?: {
    name?: string | null;
    canonical_name?: string | null;
    target_type?: string | null;
    parent_id?: number | null;
    status?: string | null;
  };
};

const REGION_NAMES = new Intl.DisplayNames(['en'], { type: 'region' });

const countryDisplayName = (code: string | null | undefined): string | null => {
  if (!code) return null;
  try {
    return REGION_NAMES.of(code.toUpperCase()) ?? code;
  } catch {
    return code;
  }
};

/** Stable identity signature for an active row — used both to diff the draft
 * against the saved set (dirty detection) and to dedupe. Radius includes the
 * editable fields so an in-place radius tweak registers as a change. */
function rowSignature(row: BusinessLocationRead): string {
  if (row.variant === 'global') return 'global';
  if (row.variant === 'radius') {
    const lat = row.latitude != null ? Number(row.latitude).toFixed(3) : '?';
    const lng = row.longitude != null ? Number(row.longitude).toFixed(3) : '?';
    return `radius:${lat}:${lng}:${row.radius_km}:${row.radius_address ?? ''}`;
  }
  return `${row.variant}:${row.location_id}`;
}

function signatureSet(rows: BusinessLocationRead[]): string {
  return rows
    .map(rowSignature)
    .sort()
    .join('|');
}

function newUuid(): string {
  return `new:${crypto.randomUUID()}`;
}

function nowIso(): string {
  return new Date().toISOString();
}

/** Build a synthetic active draft row from a catalog Location selection. */
function catalogDraftRow(loc: Location): DraftRow {
  const isCountry = loc.target_type?.toLowerCase() === 'country';
  return {
    uuid: newUuid(),
    tier: isCountry ? 'national' : 'regional',
    variant: isCountry ? 'country' : 'admin',
    country_code: loc.country_code?.toUpperCase() ?? null,
    location_id: loc.criteria_id,
    latitude: null,
    longitude: null,
    radius_km: null,
    radius_address: null,
    radius_unit: null,
    source: 'user',
    dismissed_by_user: false,
    created_at: nowIso(),
    updated_at: nowIso(),
    is_resolvable: true,
    linked_campaigns: [],
    _new: true,
    _meta: {
      name: loc.name,
      canonical_name: loc.canonical_name,
      target_type: loc.target_type,
      parent_id: loc.parent_id,
      status: loc.status,
    },
  };
}

function radiusDraftRow(r: RadiusFormValue): DraftRow {
  return {
    uuid: newUuid(),
    tier: 'regional',
    variant: 'radius',
    country_code: r.countryCode ? r.countryCode.toUpperCase() : null,
    location_id: null,
    latitude: r.lat,
    longitude: r.lng,
    radius_km: r.radiusKm,
    radius_address: r.formattedAddress,
    radius_unit: r.unit ?? 'km',
    source: 'user',
    dismissed_by_user: false,
    created_at: nowIso(),
    updated_at: nowIso(),
    is_resolvable: true,
    linked_campaigns: [],
    _new: true,
  };
}

function rowToReconcileItem(row: DraftRow): BusinessLocationReconcileItem {
  return {
    uuid: row._new ? undefined : row.uuid,
    tier: row.tier,
    variant: row.variant,
    country_code: row.country_code,
    location_id: row.location_id,
    latitude: row.latitude,
    longitude: row.longitude,
    radius_km: row.radius_km,
    radius_address: row.radius_address,
    radius_unit: row.radius_unit,
    ...(row._meta ?? {}),
  };
}

export function BusinessLocations({ business, onDirtyChange, saveRef }: BusinessLocationsProps) {
  const { user } = useUserProfile();
  const isAdmin = user?.role === UserRole.ADMIN;
  const isTestMode = useTestMode();
  const [showDismissed, setShowDismissed] = useState(false);

  return (
    <BusinessLocationsCard
      business={business}
      isAdmin={isAdmin}
      isTestMode={isTestMode}
      showDismissed={showDismissed}
      setShowDismissed={setShowDismissed}
      onDirtyChange={onDirtyChange}
      saveRef={saveRef}
    />
  );
}

interface CardProps {
  business: Business;
  isAdmin: boolean;
  isTestMode: boolean;
  showDismissed: boolean;
  setShowDismissed: (v: boolean) => void;
  onDirtyChange?: (dirty: boolean) => void;
  saveRef?: React.MutableRefObject<LocationsSaveControl | null>;
}

function BusinessLocationsCard({
  business,
  isAdmin,
  isTestMode,
  showDismissed,
  setShowDismissed,
  onDirtyChange,
  saveRef,
}: CardProps) {
  const queryClient = useQueryClient();
  const { data: serverRows } = useSuspenseQuery(
    businessLocationsQueryOptions(business.id, showDismissed),
  );

  // Saved active set = the baseline the draft is diffed against. Dismissed rows
  // (shown only via the test-mode "show dismissed" toggle) stay server-driven.
  const serverActive = useMemo(
    () => serverRows.filter((r) => !r.dismissed_by_user),
    [serverRows],
  );

  // ---- Local draft (staged until the page's "Save Changes") --------------
  const [draft, setDraft] = useState<DraftRow[]>(serverActive);
  // Reseed the draft whenever the saved set changes (initial load, post-save
  // refetch, or a secondary admin action). No primary edit hits the server
  // directly anymore, so this can't clobber in-flight staging.
  const lastBaseline = useRef<string>(signatureSet(serverActive));
  useEffect(() => {
    const sig = signatureSet(serverActive);
    if (sig !== lastBaseline.current) {
      lastBaseline.current = sig;
      setDraft(serverActive);
    }
  }, [serverActive]);

  const isDirty = useMemo(
    () => signatureSet(draft) !== signatureSet(serverActive),
    [draft, serverActive],
  );
  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  // Expose the save controller to the page. The reconcile now happens inside the parent's atomic
  // save (#5401): the parent takes getPayload() into the one request, then calls markSaved() with
  // the server's active set so this component adopts it as the new baseline (draft was owned here).
  useEffect(() => {
    if (!saveRef) return;
    saveRef.current = {
      getPayload: () => draft.map(rowToReconcileItem),
      markSaved: (result) => {
        lastBaseline.current = signatureSet(result);
        setDraft(result);
        // Refresh the canonical query (and its show-dismissed variant) + any
        // consumers of the business object.
        queryClient.invalidateQueries({ queryKey: ['business-locations', business.id] });
        queryClient.invalidateQueries({
          predicate: (q) => q.queryKey[0] === 'organizations' && q.queryKey[2] === 'business',
        });
      },
    };
    return () => {
      if (saveRef.current) saveRef.current = null;
    };
  }, [saveRef, draft, business.id, queryClient]);

  // ---- Draft mutators (replace the old immediate mutations) ---------------
  const addCatalog = (loc: Location) =>
    setDraft((cur) => {
      if (cur.some((r) => r.variant !== 'radius' && r.variant !== 'global' && r.location_id === loc.criteria_id)) {
        return cur; // already present
      }
      // Specific targeting overrides Worldwide (mirrors the backend invariant).
      const withoutGlobal = cur.filter((r) => r.variant !== 'global');
      return [...withoutGlobal, catalogDraftRow(loc)];
    });

  const removeCatalog = (criteriaId: number) =>
    setDraft((cur) => cur.filter((r) => !(r.variant !== 'radius' && r.location_id === criteriaId)));

  const addRadius = (r: RadiusFormValue) =>
    setDraft((cur) => {
      if (cur.some((x) => x.variant === 'radius' && x.latitude === r.lat && x.longitude === r.lng)) {
        return cur;
      }
      const withoutGlobal = cur.filter((x) => x.variant !== 'global');
      return [...withoutGlobal, radiusDraftRow(r)];
    });

  const removeRadiusByCoords = (lat: number, lng: number) =>
    setDraft((cur) =>
      cur.filter((r) => !(r.variant === 'radius' && r.latitude === lat && r.longitude === lng)),
    );

  const editRadius = (lat: number, lng: number, next: RadiusFormValue) =>
    setDraft((cur) =>
      cur.map((r) =>
        r.variant === 'radius' && r.latitude === lat && r.longitude === lng
          ? {
              ...r,
              country_code: next.countryCode ? next.countryCode.toUpperCase() : null,
              latitude: next.lat,
              longitude: next.lng,
              radius_km: next.radiusKm,
              radius_address: next.formattedAddress,
              radius_unit: next.unit ?? 'km',
              updated_at: nowIso(),
            }
          : r,
      ),
    );

  const removeRow = (uuid: string) => setDraft((cur) => cur.filter((r) => r.uuid !== uuid));

  const targetWorldwide = () =>
    setDraft([
      {
        uuid: newUuid(),
        tier: 'global',
        variant: 'global',
        country_code: null,
        location_id: null,
        latitude: null,
        longitude: null,
        radius_km: null,
        radius_address: null,
        radius_unit: null,
        source: 'user',
        dismissed_by_user: false,
        created_at: nowIso(),
        updated_at: nowIso(),
        is_resolvable: true,
        linked_campaigns: [],
        _new: true,
      },
    ]);

  // ---- Secondary, still-immediate admin actions (test-mode) ---------------
  const reset = useResetLocationToAgentMutation(business.id);
  const handleResetToAgent = (uuid: string) => reset.mutate(uuid);

  // Geo names/reach for catalog rows.
  const locationIds = useMemo(
    () => draft.map((r) => r.location_id).filter((id): id is number => id != null),
    [draft],
  );
  const geoLookup = useQuery(locationsByIdsQueryOptions(locationIds));
  const geoTargetById = useMemo(() => {
    const map = new Map<number, Location>();
    (geoLookup.data ?? []).forEach((g: Location) => map.set(g.criteria_id, g));
    return map;
  }, [geoLookup.data]);

  // Derived working sets handed to the map dialog (which now edits the draft via
  // callbacks instead of hitting the server).
  const draftCatalogLocations = useMemo<Location[]>(
    () =>
      draft
        .filter((r) => (r.variant === 'country' || r.variant === 'admin') && r.location_id != null)
        .map((r) => {
          const geo = geoTargetById.get(r.location_id as number);
          return (
            geo ?? {
              criteria_id: r.location_id as number,
              name: r._meta?.name ?? '',
              canonical_name: r._meta?.canonical_name ?? '',
              country_code: r.country_code,
              target_type: r._meta?.target_type ?? (r.variant === 'country' ? 'Country' : 'Region'),
              parent_id: r._meta?.parent_id ?? null,
              status: r._meta?.status ?? 'ACTIVE',
            }
          ) as Location;
        }),
    [draft, geoTargetById],
  );
  const draftRadii = useMemo<RadiusFormValue[]>(
    () =>
      draft
        .filter((r) => r.variant === 'radius' && r.latitude != null && r.longitude != null && r.radius_km != null)
        .map((r) => ({
          lat: r.latitude as number,
          lng: r.longitude as number,
          radiusKm: r.radius_km as number,
          countryCode: r.country_code,
          formattedAddress: r.radius_address ?? `${r.latitude}, ${r.longitude}`,
          unit: r.radius_unit ?? 'km',
        })),
    [draft],
  );

  const globalRow = useMemo(() => draft.find((r) => r.variant === 'global') ?? null, [draft]);
  const countryRows = useMemo(() => draft.filter((r) => r.variant === 'country'), [draft]);
  const adminRows = useMemo(() => draft.filter((r) => r.variant === 'admin'), [draft]);

  // Catalog locations a campaign positively targets can't be removed here — the
  // backend reconcile rejects it (#4816). The list rows already lock delete; the
  // picker must do the same, so pass these criteria ids down for it to lock.
  const campaignLockedCriteriaIds = useMemo(
    () =>
      new Set(
        draft
          .filter((r) => (r.linked_campaigns?.length ?? 0) > 0 && r.location_id != null)
          .map((r) => r.location_id as number),
      ),
    [draft],
  );

  const [radiusSort] = useState<RadiusSort>('created');
  const radiusRows = useMemo(() => {
    const arr = draft.filter((r) => r.variant === 'radius');
    if (radiusSort === 'name') {
      arr.sort((a, b) => (a.radius_address ?? '').localeCompare(b.radius_address ?? ''));
    } else if (radiusSort === 'radius') {
      arr.sort((a, b) => (a.radius_km ?? 0) - (b.radius_km ?? 0));
    } else {
      arr.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    }
    return arr;
  }, [draft, radiusSort]);

  const [addOpen, setAddOpen] = useState(false);
  const [editRadiusRow, setEditRadiusRow] = useState<BusinessLocationRead | null>(null);
  const [sort, setSort] = useState<{ key: 'type' | 'campaign' | 'reach'; dir: 'asc' | 'desc' } | null>(null);
  const toggleSort = (key: 'type' | 'campaign' | 'reach') =>
    setSort((s) => (s?.key === key ? (s.dir === 'asc' ? { key, dir: 'desc' } : null) : { key, dir: 'asc' }));
  const [resolving, setResolving] = useState<BusinessLocationRead | null>(null);

  const renderCountryRow = (row: DraftRow) => {
    const geo = row.location_id ? geoTargetById.get(row.location_id) : undefined;
    const label = geo?.canonical_name
      ? formatLocationName(geo.canonical_name)
      : (countryDisplayName(row.country_code) ?? 'Country');
    return (
      <LocationRow
        key={row.uuid}
        row={row}
        label={label}
        subLabel=""
        typeLabel="Location"
        reach={geo?.reach}
        onDismiss={removeRow}
        onResetToAgent={handleResetToAgent}
        onResolve={setResolving}
      />
    );
  };

  const renderAdminRow = (row: DraftRow) => {
    const geo = row.location_id ? geoTargetById.get(row.location_id) : undefined;
    // Prefer the resolved geo name; fall back to the name carried on the draft (_meta,
    // from the picker's by-country data) so freshly-added regions aren't all "Region GB"
    // before the by-ids lookup / save-time materialization resolves them.
    const metaName = row._meta?.canonical_name ?? row._meta?.name;
    const label = geo?.canonical_name
      ? formatLocationName(geo.canonical_name)
      : metaName
        ? formatLocationName(metaName)
        : `Region ${row.country_code ?? '?'}`;
    return (
      <LocationRow
        key={row.uuid}
        row={row}
        label={label}
        subLabel=""
        typeLabel="Location"
        reach={geo?.reach}
        onDismiss={removeRow}
        onResetToAgent={handleResetToAgent}
        onResolve={setResolving}
      />
    );
  };

  const renderRadiusRow = (row: DraftRow) => {
    const label = row.radius_address ?? `${row.latitude}, ${row.longitude}`;
    const lat = row.latitude != null ? Number(row.latitude).toFixed(5) : null;
    const lng = row.longitude != null ? Number(row.longitude).toFixed(5) : null;
    const coords = lat && lng ? `${lat}, ${lng}` : null;
    // #5497: show the radius in the unit it was saved in (per-row), so it survives reload.
    const unit = row.radius_unit ?? 'km';
    const distance = row.radius_km != null ? `${radiusInUnit(row.radius_km, unit)} ${unit}` : null;
    const parts = [distance, coords, row.country_code].filter(Boolean);
    const subLabel = parts.join(' - ');
    return (
      <LocationRow
        key={row.uuid}
        row={row}
        label={label}
        subLabel={subLabel}
        typeLabel="Radius"
        onDismiss={removeRow}
        onResetToAgent={handleResetToAgent}
        onEdit={() => {
          setEditRadiusRow(row);
          setAddOpen(true);
        }}
      />
    );
  };

  const renderGlobalRow = (row: DraftRow) => (
    <LocationRow
      key={row.uuid}
      row={row}
      label="Worldwide"
      subLabel="Targets everywhere"
      typeLabel="Worldwide"
      onDismiss={removeRow}
    />
  );

  const rowItems = useMemo(() => {
    const items: {
      row: DraftRow;
      typeLabel: string;
      campaignLabel: string;
      reach: number | null;
      kind: 'global' | 'country' | 'admin' | 'radius';
    }[] = [];
    // Only geo (country/admin) rows have a catalog reach; radius/global have none.
    const reachOf = (r: DraftRow) => (r.location_id ? (geoTargetById.get(r.location_id)?.reach ?? null) : null);
    if (globalRow) {
      items.push({ row: globalRow, typeLabel: 'Worldwide', campaignLabel: '', reach: null, kind: 'global' });
    }
    countryRows.forEach((r) =>
      items.push({ row: r, typeLabel: 'Location', campaignLabel: r.linked_campaigns?.[0]?.name ?? '', reach: reachOf(r), kind: 'country' }),
    );
    adminRows.forEach((r) =>
      items.push({ row: r, typeLabel: 'Location', campaignLabel: r.linked_campaigns?.[0]?.name ?? '', reach: reachOf(r), kind: 'admin' }),
    );
    radiusRows.forEach((r) =>
      items.push({ row: r, typeLabel: 'Radius', campaignLabel: r.linked_campaigns?.[0]?.name ?? '', reach: null, kind: 'radius' }),
    );
    return items;
  }, [globalRow, countryRows, adminRows, radiusRows, geoTargetById]);

  const sortedItems = useMemo(() => {
    if (!sort) return rowItems;
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...rowItems].sort((a, b) => {
      if (sort.key === 'reach') {
        // Numeric sort; rows without reach ("-") always sink to the bottom.
        if (a.reach == null && b.reach == null) return 0;
        if (a.reach == null) return 1;
        if (b.reach == null) return -1;
        return (a.reach - b.reach) * dir;
      }
      const val = (i: (typeof rowItems)[number]) => (sort.key === 'type' ? i.typeLabel : i.campaignLabel);
      return val(a).localeCompare(val(b)) * dir;
    });
  }, [rowItems, sort]);

  const renderItem = (item: (typeof rowItems)[number]) =>
    item.kind === 'global'
      ? renderGlobalRow(item.row)
      : item.kind === 'country'
        ? renderCountryRow(item.row)
        : item.kind === 'admin'
          ? renderAdminRow(item.row)
          : renderRadiusRow(item.row);

  const sortIcon = (key: 'type' | 'campaign' | 'reach') =>
    sort?.key !== key ? (
      <ArrowUpDown className="h-3 w-3 opacity-40" />
    ) : sort.dir === 'asc' ? (
      <ArrowUp className="h-3 w-3" />
    ) : (
      <ArrowDown className="h-3 w-3" />
    );

  return (
    <div className="space-y-3 min-w-0" data-testid="business-locations-card">
      {business.backfill_truncated_at && (
        <TruncationBanner businessId={business.id} isAdmin={isAdmin} truncatedAt={business.backfill_truncated_at} />
      )}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Locations</h3>
            <p className="text-sm text-gray-700 mt-1">Geographic areas you serve</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              className="text-gray-900"
              onClick={() => setAddOpen(true)}
              data-testid="add-location-button"
            >
              <Plus className="mr-2 h-4 w-4" />
              Add
            </Button>
            {isTestMode && (
              <DetectorStatus
                businessId={business.id}
                currentRowCount={draft.length}
                render={({ onRunDetect, onBackfill, isRunning, isStarting }) => (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="icon" className="text-gray-900 h-9 w-9">
                        {isRunning || isStarting ? (
                          <Spinner size="sm" />
                        ) : (
                          <MoreHorizontal className="h-4 w-4" />
                        )}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => setShowDismissed(!showDismissed)}>
                        <ArchiveRestore className="mr-2 h-4 w-4" />
                        {showDismissed ? 'Hide dismissed' : 'Show dismissed'}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={onRunDetect} disabled={isRunning || isStarting || isDirty}>
                        <Sparkles className="mr-2 h-4 w-4" />
                        Re-run detection
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={onBackfill} disabled={isRunning || isStarting || isDirty}>
                        <RefreshCw className={`mr-2 h-4 w-4 ${isRunning || isStarting ? 'animate-spin' : ''}`} />
                        Backfill from history
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              />
            )}
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
          {rowItems.length > 0 && (
            <div className="flex items-center gap-3 border-b border-gray-200 bg-gray-50 px-4 py-2 text-[10px] font-semibold uppercase tracking-wider text-gray-500">
              <span className="h-5 w-5 flex-shrink-0" />
              <span className="w-1/3 min-w-0">Location</span>
              <button
                type="button"
                onClick={() => toggleSort('type')}
                className="w-28 flex-shrink-0 flex items-center gap-1 uppercase hover:text-gray-900"
              >
                Type {sortIcon('type')}
              </button>
              <button
                type="button"
                onClick={() => toggleSort('campaign')}
                className="flex-1 min-w-0 flex items-center gap-1 uppercase hover:text-gray-900"
              >
                Campaign {sortIcon('campaign')}
              </button>
              <button
                type="button"
                onClick={() => toggleSort('reach')}
                className="w-24 flex-shrink-0 flex items-center gap-1 pl-8 uppercase hover:text-gray-900"
              >
                Reach {sortIcon('reach')}
              </button>
              <span className="w-28 flex-shrink-0" />
            </div>
          )}
          {sortedItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center px-6 py-12 text-center">
              <Globe className="mb-4 h-10 w-10 text-gray-400" strokeWidth={1.5} />
              <h4 className="mb-1 text-base font-semibold text-gray-900">Serving Everywhere</h4>
              <p className="mb-4 max-w-sm text-sm text-gray-700">
                With no locations set, ads can appear worldwide. Add a country, region, or radius to narrow where you
                appear, or confirm worldwide targeting.
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  className="text-gray-900"
                  onClick={() => setAddOpen(true)}
                  data-testid="add-location-empty-button"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add location
                </Button>
                <Button
                  variant="outline"
                  className="text-gray-900"
                  onClick={targetWorldwide}
                  data-testid="target-worldwide-empty-button"
                >
                  <Globe className="mr-2 h-4 w-4" />
                  Target Worldwide
                </Button>
              </div>
            </div>
          ) : (
            sortedItems.map(renderItem)
          )}
        </div>
      </div>

      <MapLocationDialog
        open={addOpen}
        businessId={business.id}
        editRadius={editRadiusRow}
        catalogSelected={draftCatalogLocations}
        lockedCriteriaIds={campaignLockedCriteriaIds}
        radii={draftRadii}
        onAddCatalog={addCatalog}
        onRemoveCatalog={removeCatalog}
        onAddRadius={addRadius}
        onRemoveRadius={removeRadiusByCoords}
        onEditRadius={editRadius}
        onClose={() => {
          setAddOpen(false);
          setEditRadiusRow(null);
        }}
      />
      <ResolveOrphanDialog
        open={!!resolving}
        businessId={business.id}
        row={resolving}
        onClose={() => setResolving(null)}
      />
    </div>
  );
}
